import type { OrganizationRegion } from './adminAccess.js';

export interface HeadquartersAddress {
    addressLine1: string;
    addressLine2?: string;
    city: string;
    administrativeArea?: string;
    postalCode?: string;
    country: string;
    countryCode?: string;
}

const countriesByRegion: Record<Exclude<OrganizationRegion, 'Global'>, string[]> = {
    'North America': [
        'Anguilla', 'Antigua and Barbuda', 'Aruba', 'Bahamas', 'Barbados', 'Belize', 'Bermuda',
        'Bonaire, Sint Eustatius and Saba', 'British Virgin Islands', 'Canada', 'Cayman Islands',
        'Costa Rica', 'Cuba', 'Curaçao', 'Dominica', 'Dominican Republic', 'El Salvador', 'Greenland',
        'Grenada', 'Guadeloupe', 'Guatemala', 'Haiti', 'Honduras', 'Jamaica', 'Martinique', 'Mexico',
        'Montserrat', 'Nicaragua', 'Panama', 'Puerto Rico', 'Saint Barthélemy', 'Saint Kitts and Nevis',
        'Saint Lucia', 'Saint Martin', 'Saint Pierre and Miquelon', 'Saint Vincent and the Grenadines',
        'Sint Maarten', 'Trinidad and Tobago', 'Turks and Caicos Islands', 'United States', 'US Virgin Islands',
    ],
    'South America': [
        'Argentina', 'Bolivia', 'Brazil', 'Chile', 'Colombia', 'Ecuador', 'Falkland Islands', 'French Guiana',
        'Guyana', 'Paraguay', 'Peru', 'South Georgia and South Sandwich Islands', 'Suriname', 'Uruguay', 'Venezuela',
    ],
    'Oceania': [
        'American Samoa', 'Australia', 'Christmas Island', 'Cocos (Keeling) Islands', 'Cook Islands', 'Fiji',
        'French Polynesia', 'Guam', 'Kiribati', 'Marshall Islands', 'Micronesia', 'Nauru', 'New Caledonia',
        'New Zealand', 'Niue', 'Norfolk Island', 'Northern Mariana Islands', 'Palau', 'Papua New Guinea',
        'Pitcairn Islands', 'Samoa', 'Solomon Islands', 'Tokelau', 'Tonga', 'Tuvalu',
        'United States Minor Outlying Islands', 'Vanuatu', 'Wallis and Futuna',
    ],
    'Africa': [
        'Algeria', 'Angola', 'Benin', 'Botswana', 'Bouvet Island', 'Burkina Faso', 'Burundi', 'Cabo Verde',
        'Cameroon', 'Central African Republic', 'Chad', 'Comoros', 'Congo, Democratic Republic of the',
        'Congo, Republic of the', "Cote d'Ivoire", 'Djibouti', 'Equatorial Guinea', 'Eritrea', 'Eswatini',
        'Ethiopia', 'Gabon', 'Gambia', 'Ghana', 'Guinea', 'Guinea-Bissau', 'Kenya', 'Lesotho', 'Liberia',
        'Libya', 'Madagascar', 'Malawi', 'Mali', 'Mauritania', 'Mauritius', 'Mayotte', 'Morocco', 'Mozambique',
        'Namibia', 'Niger', 'Nigeria', 'Réunion', 'Rwanda', 'Saint Helena, Ascension and Tristan da Cunha',
        'Sao Tome and Principe', 'Senegal', 'Seychelles', 'Sierra Leone', 'Somalia', 'South Africa',
        'South Sudan', 'Sudan', 'Tanzania', 'Togo', 'Tunisia', 'Uganda', 'Western Sahara', 'Zambia', 'Zimbabwe',
    ],
    'Middle East': [
        'Bahrain', 'Egypt', 'Iran', 'Iraq', 'Israel', 'Jordan', 'Kuwait', 'Lebanon', 'Oman', 'Palestine',
        'Qatar', 'Saudi Arabia', 'Syria', 'Turkey', 'United Arab Emirates', 'Yemen',
    ],
    'Europe': [
        'Åland Islands', 'Albania', 'Andorra', 'Austria', 'Belarus', 'Belgium', 'Bosnia and Herzegovina',
        'Bulgaria', 'Croatia', 'Cyprus', 'Czech Republic', 'Denmark', 'Estonia', 'Faroe Islands', 'Finland',
        'France', 'Germany', 'Gibraltar', 'Greece', 'Guernsey', 'Hungary', 'Iceland', 'Ireland', 'Isle of Man',
        'Italy', 'Jersey', 'Kosovo', 'Latvia', 'Liechtenstein', 'Lithuania', 'Luxembourg', 'Malta', 'Moldova',
        'Monaco', 'Montenegro', 'Netherlands', 'North Macedonia', 'Norway', 'Poland', 'Portugal', 'Romania',
        'Russia', 'San Marino', 'Serbia', 'Slovakia', 'Slovenia', 'Spain', 'Svalbard and Jan Mayen', 'Sweden',
        'Switzerland', 'Ukraine', 'United Kingdom', 'Vatican City',
    ],
    'Asia': [
        'Afghanistan', 'Armenia', 'Azerbaijan', 'Bangladesh', 'Bhutan', 'British Indian Ocean Territory',
        'Brunei', 'Cambodia', 'China', 'Georgia', 'Hong Kong', 'India', 'Indonesia', 'Japan', 'Kazakhstan',
        'Kyrgyzstan', 'Laos', 'Macao', 'Malaysia', 'Maldives', 'Mongolia', 'Myanmar', 'Nepal', 'North Korea',
        'Pakistan', 'Philippines', 'Singapore', 'South Korea', 'Sri Lanka', 'Taiwan', 'Tajikistan', 'Thailand',
        'Timor-Leste', 'Turkmenistan', 'Uzbekistan', 'Vietnam',
    ],
};

function key(value: unknown): string {
    return String(value || '').trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[’]/g, "'").toLowerCase();
}

const regionByCountry = new Map<string, OrganizationRegion>();
for (const [region, countries] of Object.entries(countriesByRegion))
    for (const country of countries)
        regionByCountry.set(key(country), region as OrganizationRegion);

export function cleanHeadquartersAddress(value: any): HeadquartersAddress {
    return {
        addressLine1: String(value?.addressLine1 || '').trim().slice(0, 300),
        addressLine2: String(value?.addressLine2 || '').trim().slice(0, 300),
        city: String(value?.city || '').trim().slice(0, 150),
        administrativeArea: String(value?.administrativeArea || '').trim().slice(0, 150),
        postalCode: String(value?.postalCode || '').trim().slice(0, 40),
        country: String(value?.country || '').trim().slice(0, 150),
        countryCode: String(value?.countryCode || '').trim().slice(0, 2).toUpperCase(),
    };
}

export function hasAnyHeadquartersAddress(address: HeadquartersAddress): boolean {
    return Object.values(address).some(Boolean);
}

export function hasCompleteHeadquartersAddress(address: HeadquartersAddress): boolean {
    return Boolean(address.addressLine1 && address.city && address.country && regionByCountry.has(key(address.country)));
}

export function deriveOrganizationRegion(address: HeadquartersAddress): OrganizationRegion {
    if (!hasCompleteHeadquartersAddress(address))
        return 'Global';
    return regionByCountry.get(key(address.country)) || 'Global';
}

export function regionForCountry(country: string): OrganizationRegion {
    return regionByCountry.get(key(country)) || 'Global';
}
